// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers. The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";

// Define the type for Google Analytics handler configuration
export type xGoogleAnalyticsHandlerType = {
    trackGoogleAnalytics?: boolean;
    // Basic Events
    checkoutFlowEvents?: boolean;
    addToCartEvent?: boolean;
    addToCartEventName?: string;
    purchaseEvent?: boolean;
    purchaseEventName?: string;
    removeFromCartLineEvent?: boolean;
    removeFromCartLineEventName?: string;
    pdpViewEvent?: boolean;
    pdpViewEventName?: string;
    productImpressionEvent?: boolean;
    productImpressionEventName?: string;
    selectItemEvent?: boolean;
    selectItemEventName?: string;
    selectPromotionEvent?: boolean;
    selectPromotionEventName?: string;
    searchEvent?: boolean;
    searchEventName?: string;
    noStockSearchEvent?: boolean;
    noStockSearchEventName?: string;
    viewCartEvent?: boolean;
    viewCartEventName?: string;
    // Settings
    includeProductBrand?: boolean;
    includeProductCategory?: boolean;
    // Flags
    shouldTrackPayment?: boolean;
    shouldTrackBeginOnePageCheckout?: boolean;
    shouldTrackNoStockSearch?: boolean;
    affiliation?: string;
    // Custom Events
    loginEvent?: boolean;
    loginEventName?: string;
    setDasboardAsHomepageEvent?: boolean;
    setDasboardAsHomepageEventName?: string;
    returnRequestEvent?: boolean;
    returnRequestEventName?: string;
};

// Define the state type for Google Analytics
export type xGoogleAnalyticsStateType = Readonly<xGoogleAnalyticsHandlerType>;

// Initialize the default state for Google Analytics
const initialXGoogleAnalytics: xGoogleAnalyticsStateType = {
    trackGoogleAnalytics: false,
    // Basic Events
    checkoutFlowEvents: false,
    addToCartEvent: false,
    addToCartEventName: "add_to_cart",
    purchaseEvent: false,
    purchaseEventName: "purchase",
    removeFromCartLineEvent: false,
    removeFromCartLineEventName: "remove_from_cart",
    pdpViewEvent: false,
    pdpViewEventName: "view_item",
    productImpressionEvent: false,
    productImpressionEventName: "view_item_list",
    selectItemEvent: false,
    selectItemEventName: "select_item",
    selectPromotionEvent: false,
    selectPromotionEventName: "",
    searchEvent: false,
    searchEventName: "search",
    noStockSearchEvent: false,
    noStockSearchEventName: "nostock_search",
    viewCartEvent: false,
    viewCartEventName: "view_cart",
    // Settings
    includeProductBrand: false,
    includeProductCategory: false,
    // Flags
    shouldTrackPayment: false,
    shouldTrackBeginOnePageCheckout: false,
    shouldTrackNoStockSearch: false,
    affiliation: "",
    // Custom Events
    loginEvent: false,
    loginEventName: "login",
    setDasboardAsHomepageEvent: false,
    setDasboardAsHomepageEventName: "make_dash_hmpg",
    returnRequestEvent: false,
    returnRequestEventName: "return_request",
} as xGoogleAnalyticsStateType;

// Define the reducer to handle Google Analytics actions
const reducer = {
    // Handle updating Google Analytics configuration
    "xGoogleAnalytics/update": (draft: Draft<xGoogleAnalyticsStateType>, action: { trackGoogleAnalytics: boolean, data: xGoogleAnalyticsHandlerType }) => {
        draft.trackGoogleAnalytics = action.trackGoogleAnalytics;
        // Basic Events
        draft.checkoutFlowEvents = action.data.checkoutFlowEvents ?? false;
        draft.addToCartEvent = action.data.addToCartEvent ?? false;
        draft.addToCartEventName = action.data.addToCartEventName ?? "add_to_cart";
        draft.purchaseEvent = action.data.purchaseEvent ?? false;
        draft.purchaseEventName = action.data.purchaseEventName ?? "purchase";
        draft.removeFromCartLineEvent = action.data.removeFromCartLineEvent ?? false;
        draft.removeFromCartLineEventName = action.data.removeFromCartLineEventName ?? "remove_from_cart";
        draft.pdpViewEvent = action.data.pdpViewEvent ?? false;
        draft.pdpViewEventName = action.data.pdpViewEventName ?? "view_item";
        draft.productImpressionEvent = action.data.productImpressionEvent ?? false;
        draft.productImpressionEventName = action.data.productImpressionEventName ?? "view_item_list";
        draft.selectItemEvent = action.data.selectItemEvent ?? false;
        draft.selectItemEventName = action.data.selectItemEventName ?? "select_item";
        draft.selectPromotionEvent = action.data.selectPromotionEvent ?? false;
        draft.selectPromotionEventName = action.data.selectPromotionEventName ?? "selectPromotionEventName";
        draft.searchEvent = action.data.searchEvent ?? false;
        draft.searchEventName = action.data.searchEventName ?? "search";
        draft.noStockSearchEvent = action.data.noStockSearchEvent ?? false;
        draft.noStockSearchEventName = action.data.noStockSearchEventName ?? "nostock_search";
        draft.viewCartEvent = action.data.viewCartEvent ?? false;
        draft.viewCartEventName = action.data.viewCartEventName ?? "view_cart";
        // Settings
        draft.includeProductBrand = action.data.includeProductBrand ?? false;
        draft.includeProductCategory = action.data.includeProductCategory ?? false;
        // Custom Events
        draft.loginEvent = action.data.loginEvent ?? false;
        draft.loginEventName = action.data.loginEventName ?? "login";
        draft.setDasboardAsHomepageEvent = action.data.setDasboardAsHomepageEvent ?? false;
        draft.setDasboardAsHomepageEventName = action.data.setDasboardAsHomepageEventName ?? "make_dash_hmpg";
        draft.returnRequestEvent = action.data.returnRequestEvent ?? false;
        draft.returnRequestEventName = action.data.returnRequestEventName ?? "view_product_list";
    },
    // Handle setting the payment tracking flag
    "xGoogleAnalytics/setShouldTrackPayment": (draft: Draft<xGoogleAnalyticsStateType>, action: { shouldTrackPayment: boolean }) => {
        draft.shouldTrackPayment = action.shouldTrackPayment;
    },
    // Handle setting the payment tracking flag
    "xGoogleAnalytics/setShouldTrackBeginOnePageCheckout": (draft: Draft<xGoogleAnalyticsStateType>, action: { shouldTrackBeginOnePageCheckout: boolean }) => {
        draft.shouldTrackBeginOnePageCheckout = action.shouldTrackBeginOnePageCheckout;
    },
    // Handle setting the nostock search tracking flag
    "xGoogleAnalytics/setShouldTrackNoStockSearch": (draft: Draft<xGoogleAnalyticsStateType>, action: { shouldTrackNoStockSearch: boolean }) => {
        draft.shouldTrackNoStockSearch = action.shouldTrackNoStockSearch;
    },
    // Handle setting the affiliation
    "xGoogleAnalytics/setAffiliation": (draft: Draft<xGoogleAnalyticsStateType>, action: { affiliation: string }) => {
        draft.affiliation = action.affiliation;
    },
};

// Create the Google Analytics reducer using Immer for immutability
const XGoogleAnalytics = createTypedReducerWithImmer(initialXGoogleAnalytics, reducer);

// Add the Google Analytics reducer to the application's reducers
(reducers as any).xGoogleAnalytics = XGoogleAnalytics;

// Define the application state including Google Analytics state
export type xGoogleAnalyticsState = {
    xGoogleAnalytics: xGoogleAnalyticsStateType;
} & ApplicationState;

// Define the type for Google Analytics reducers
type xGoogleAnalyticsReducers = {
    [key: string]: typeof XGoogleAnalytics;
};

// Define the type for Google Analytics actions
export type xGoogleAnalyticsActions = Parameters<xGoogleAnalyticsReducers[keyof xGoogleAnalyticsReducers]>[1] | AnyAction;
